/**
 * Bottom Dock Navigation with Mega Menu
 * Handles interactions, animations, and accessibility
 */

class BottomDockNavigation extends HTMLElement {
  constructor() {
    super();
    
    this.isOpen = false;
    this.lastScrollY = window.scrollY;
    
    // Wait for DOM to be ready before initializing
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      // Use setTimeout to ensure all elements are rendered
      setTimeout(() => this.init(), 0);
    }
  }

  connectedCallback() {
    // Also initialize when element is connected to DOM
    setTimeout(() => this.init(), 0);
  }

  init() {
    // Re-query elements each time to ensure we have the latest references
    this.dock = this.querySelector('.dock');
    this.megaMenu = document.querySelector('.mega-menu') || this.querySelector('.mega-menu');
    this.categoriesButton = this.querySelector('[data-dock-trigger="categories"]');
    
    if (!this.megaMenu || !this.categoriesButton) {
      console.warn('BottomDockNavigation: Mega menu or categories button not found');
      return;
    }
    
    // Pre-load and fade in images
    this.preloadImages();
    
    // Remove existing listeners to prevent duplicates
    if (this._clickHandler) {
      this.categoriesButton.removeEventListener('click', this._clickHandler);
    }
    
    // Bind event listeners with named function for cleanup
    this._clickHandler = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.toggleMegaMenu();
    };
    
    this.categoriesButton.addEventListener('click', this._clickHandler);
    
    // Close menu when clicking outside
    if (!this._outsideClickHandler) {
      this._outsideClickHandler = (e) => {
        if (this.isOpen && 
            !this.megaMenu.contains(e.target) && 
            !this.categoriesButton.contains(e.target)) {
          this.closeMegaMenu();
        }
      };
      document.addEventListener('click', this._outsideClickHandler);
    }
    
    // Close on ESC key
    if (!this._escHandler) {
      this._escHandler = (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.closeMegaMenu();
        }
      };
      document.addEventListener('keydown', this._escHandler);
    }
    
    // Handle scroll animations
    this.handleScrollAnimations();
    
    // Update cart count dynamically
    this.setupCartCountUpdates();
  }

  toggleMegaMenu() {
    if (this.isOpen) {
      this.closeMegaMenu();
    } else {
      this.openMegaMenu();
    }
  }

  openMegaMenu() {
    // Ensure we have the latest reference to mega menu
    this.megaMenu = document.querySelector('.mega-menu') || this.querySelector('.mega-menu');
    
    if (!this.megaMenu) {
      console.error('BottomDockNavigation: Mega menu not found when trying to open');
      return;
    }
    
    // Prevent opening if already open
    if (this.isOpen) return;
    
    this.isOpen = true;
    
    // Force reflow to ensure initial state is applied
    this.megaMenu.offsetHeight;
    
    // Set aria-hidden to false to trigger CSS animation
    this.megaMenu.setAttribute('aria-hidden', 'false');
    
    if (this.categoriesButton) {
      this.categoriesButton.setAttribute('aria-expanded', 'true');
      this.categoriesButton.classList.add('dock__button--active');
    }
    
    // Trap focus in mega menu
    this.trapFocus();
    
    // Focus first link after animation starts
    const firstLink = this.megaMenu.querySelector('a');
    if (firstLink) {
      setTimeout(() => firstLink.focus(), 300);
    }
  }

  closeMegaMenu() {
    if (!this.megaMenu) {
      this.megaMenu = document.querySelector('.mega-menu') || this.querySelector('.mega-menu');
    }
    
    if (!this.megaMenu || !this.isOpen) return;
    
    this.isOpen = false;
    
    // Set aria-hidden to true to trigger slide down animation
    this.megaMenu.setAttribute('aria-hidden', 'true');
    
    if (this.categoriesButton) {
      this.categoriesButton.setAttribute('aria-expanded', 'false');
      this.categoriesButton.classList.remove('dock__button--active');
    }
    
    // Return focus to trigger button after animation
    if (this.categoriesButton) {
      setTimeout(() => this.categoriesButton.focus(), 350);
    }
  }

  trapFocus() {
    const focusableElements = this.megaMenu.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    if (focusableElements.length === 0) return;
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    
    if (!this._tabHandler) {
      this._tabHandler = (e) => {
        if (e.key !== 'Tab') return;
        
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      };
      this.megaMenu.addEventListener('keydown', this._tabHandler);
    }
  }

  handleScrollAnimations() {
    // Scroll animations disabled
    
    // window.addEventListener('scroll', () => {
    //   if (!ticking && typeof gsap !== 'undefined') {
    //     window.requestAnimationFrame(() => {
    //       const currentScrollY = window.scrollY;
          
    //       // Scale up slightly on scroll
    //       if (currentScrollY > this.lastScrollY && currentScrollY > 100) {
    //         gsap.to(this.dock, {
    //           scale: 1.05,
    //           duration: 0.3,
    //           ease: 'power2.out'
    //         });
    //       } else {
    //         gsap.to(this.dock, {
    //           scale: 1,
    //           duration: 0.3,
    //           ease: 'power2.out'
    //         });
    //       }
          
    //       this.lastScrollY = currentScrollY;
    //       ticking = false;
    //     });
        
    //     ticking = true;
    //   }
    // });
  }

  setupCartCountUpdates() {
    // Listen for cart updates
    document.addEventListener('cart:updated', (event) => {
      const cartCount = event.detail?.item_count || 0;
      this.updateCartCount(cartCount);
    });
    
    // Also listen for Shopify cart update events
    if (window.Shopify && window.Shopify.theme) {
      document.addEventListener('shopify:section:load', () => {
        this.fetchCartCount();
      });
    }
  }

  updateCartCount(count) {
    const badge = this.querySelector('[id^="cart-count-"]');
    if (badge) {
      badge.textContent = count;
      
      // Animate count update with GSAP if available
      if (typeof gsap !== 'undefined') {
        gsap.fromTo(
          badge,
          { scale: 1.5 },
          { scale: 1, duration: 0.3, ease: 'back.out(1.7)' }
        );
      }
    }
  }

  async fetchCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.updateCartCount(cart.item_count);
    } catch (error) {
      console.error('Error fetching cart count:', error);
    }
  }

  preloadImages() {
    if (!this.megaMenu) return;
    
    // Find all images in mega menu
    const images = this.megaMenu.querySelectorAll('img[loading="eager"]');
    
    images.forEach(img => {
      if (img.complete) {
        // Image already loaded
        img.style.opacity = '1';
      } else {
        // Add load event listener
        img.addEventListener('load', () => {
          img.style.opacity = '1';
        }, { once: true });
        
        // Add error handler
        img.addEventListener('error', () => {
          console.warn('Failed to load image:', img.src);
          img.style.opacity = '1'; // Show anyway to prevent layout issues
        }, { once: true });
      }
    });
  }
}

// Register custom element
if (!customElements.get('bottom-dock-navigation')) {
  customElements.define('bottom-dock-navigation', BottomDockNavigation);
}

// Initialize on DOM ready
function initBottomDock() {
  const dockElements = document.querySelectorAll('.bottom-dock-navigation');
  dockElements.forEach(element => {
    // Convert to custom element if not already
    if (element.tagName.toLowerCase() !== 'bottom-dock-navigation') {
      const customElement = document.createElement('bottom-dock-navigation');
      
      // Copy all attributes
      Array.from(element.attributes).forEach(attr => {
        customElement.setAttribute(attr.name, attr.value);
      });
      
      // Move all child nodes (including mega menu)
      while (element.firstChild) {
        customElement.appendChild(element.firstChild);
      }
      
      // Replace the original element
      element.parentNode.replaceChild(customElement, element);
    } else {
      // Already a custom element, just ensure it's initialized
      if (element.init && typeof element.init === 'function') {
        element.init();
      }
    }
  });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBottomDock);
} else {
  initBottomDock();
}

// Also initialize on section load (Shopify theme editor)
document.addEventListener('shopify:section:load', initBottomDock);

// Export for module usage
export { BottomDockNavigation };

